// ─────────────────────────────────────────────────────────────────────────────
// The picture editor: choose the part of a picture to keep, at exactly the
// size it is needed, in a modal.
//
// The picture moves under a fixed frame. The frame is the finished picture;
// what lies outside it is dimmed, and with `mask="circle"` so is what lies
// outside the circle a WhatsApp profile shows. It drags with a mouse, a finger
// or a pen (pointer events, with touch scrolling switched off on the picture),
// zooms with the slider, the wheel or a pinch, and works from the keyboard:
// the arrow keys move it, plus and minus zoom, 0 resets.
//
// The result is always exactly the size asked for. Space the picture does not
// reach, and any see-through pixels, are flattened onto `background`, because
// a JPG has no transparency and WhatsApp renders a transparent PNG on black.
// The maths is in `geometry.ts`, with tests; the drawing in `render.ts`.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type PointerEvent, type ReactNode } from 'react';
import { useStableId } from '@repletoryreach/sdk-utils/hooks';
import { Modal } from '../overlays/Modal/Modal.js';
import { Button } from '../core/Button/Button.js';
import { ActionIcon } from '../core/ActionIcon/ActionIcon.js';
import { Loader } from '../core/Loader/Loader.js';
import { Slider } from '../inputs/Slider/Slider.js';
import { Alert } from '../data-display/Alert/Alert.js';
import { Minus, Plus } from '../icons/builtin.js';
import {
  clampState, cropBounds, cropRects, initialState, panBy, placement, sourceEdge, sliderToZoom, zoomAt, zoomToSlider,
  type CropBounds, type CropMask, type CropState, type Point, type Rect, type Size
} from './geometry.js';
import { decodeImage, drawCrop, formatName, outputFileName, renderCrop, type DecodedImage } from './render.js';
import { DEFAULT_LABELS, fill, type ImageCropperLabels } from './labels.js';
import './ImageCropper.css';

export type ImageCropperOutputType = 'image/jpeg' | 'image/png' | 'image/webp';

export interface ImageCropperResult {
  /** The finished picture, exactly `width` × `height`. */
  file: File;
  width: number;
  height: number;
  /** Where the picture sat, to reopen the editor on the original exactly as it was left. */
  crop: CropState;
  /** The part of the original that was kept, in its own pixels. */
  source: Rect;
  /** How many source pixels the frame spanned on its shorter side. */
  sourceEdge: number;
  /** True when `sourceEdge` was under `minSourceEdge`: the result will look soft. */
  lowResolution: boolean;
}

export interface ImageCropperProps {
  /** Open while true and `file` is set. */
  opened: boolean;
  /** The picture to crop: JPEG, PNG or WebP (anything the browser decodes). */
  file: Blob | null;
  /** Cancel, Escape and the close button. Nothing is produced. */
  onCancel: () => void;
  /** "Use this picture". The modal stays open, busy, until a returned promise settles; close it here. */
  onConfirm: (result: ImageCropperResult) => void | Promise<void>;
  /** The result's width in pixels. Default 640. */
  outputWidth?: number;
  /** The result's height in pixels. Default `outputWidth / aspect`. */
  outputHeight?: number;
  /** Width over height, used when `outputHeight` is not given. Default 1. */
  aspect?: number;
  /** Fewer source pixels than this across the chosen part shows a warning that it will look blurry. 0 never warns. */
  minSourceEdge?: number;
  /** The shape the result is previewed in. Default `rect`; `circle` is how WhatsApp shows a profile picture. */
  mask?: CropMask;
  /** Fills padding and see-through pixels. Default white. */
  background?: string;
  /** Default `image/jpeg`. */
  outputType?: ImageCropperOutputType;
  /** 0 to 1, for JPEG and WebP. Default 0.9. */
  quality?: number;
  /** The result's file name; the extension follows `outputType`. Default the original's name. */
  fileName?: string;
  /** How far past "cover" the zoom goes, as a multiple of it. Default 4. */
  maxZoom?: number;
  /** Reopen with the picture where it was left (from a previous result's `crop`). */
  initialCrop?: CropState | null;
  /** Every string, English by default; pass the console's translations. */
  labels?: Partial<ImageCropperLabels>;
  /** Modal width. Default `md`. */
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | string;
  zIndex?: number;
}

const DEFAULT_BACKGROUND = '#ffffff';
/** The frame's share of the picture area, leaving a margin that shows what is cut away. */
const FRAME_SHARE = 0.84;
const PREVIEW_PX = 72;

export function ImageCropper(props: ImageCropperProps): ReactNode {
  const labels = useMemo<ImageCropperLabels>(() => ({ ...DEFAULT_LABELS, ...props.labels }), [props.labels]);
  const open = props.opened && !!props.file;
  return (
    <Modal
      opened={open}
      onClose={props.onCancel}
      title={labels.title}
      size={props.size ?? 'md'}
      closeOnClickOutside={false}
      zIndex={props.zIndex}
      className="rr-cropper-modal"
    >
      {open && props.file ? <CropperBody {...props} file={props.file} labels={labels} /> : null}
    </Modal>
  );
}

type Phase =
  | { kind: 'loading' }
  | { kind: 'ready'; image: DecodedImage; bounds: CropBounds }
  | { kind: 'error' };

function CropperBody({
  file, onCancel, onConfirm, labels,
  outputWidth = 640, outputHeight, aspect = 1, minSourceEdge = 0, mask = 'rect',
  background = DEFAULT_BACKGROUND, outputType = 'image/jpeg', quality = 0.9, fileName, maxZoom = 4, initialCrop
}: Omit<ImageCropperProps, 'labels' | 'file'> & { file: Blob; labels: ImageCropperLabels }): ReactNode {
  const frame = useMemo<Size>(() => {
    const width = Math.max(1, Math.round(outputWidth));
    const height = Math.max(1, Math.round(outputHeight ?? outputWidth / (aspect > 0 ? aspect : 1)));
    return { width, height };
  }, [outputWidth, outputHeight, aspect]);

  const [phase, setPhase] = useState<Phase>({ kind: 'loading' });
  const [crop, setCrop] = useState<CropState>({ zoom: 1, offsetX: 0, offsetY: 0 });
  const [busy, setBusy] = useState(false);
  const [exportFailed, setExportFailed] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [stageWidth, setStageWidth] = useState(0);

  const stageRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const previewRef = useRef<HTMLCanvasElement | null>(null);
  const pointers = useRef(new Map<number, Point>());
  const instructionsId = useStableId('rr-cropper-help');
  const warningId = useStableId('rr-cropper-warning');

  // Decode once per file, and free the pixels when the file changes or the editor closes.
  useEffect(() => {
    let cancelled = false;
    let decoded: DecodedImage | null = null;
    setPhase({ kind: 'loading' });
    setExportFailed(false);
    decodeImage(file).then(
      (image) => {
        if (cancelled) { image.close(); return; }
        decoded = image;
        const bounds = cropBounds(image, frame, { mask, maxCoverMultiple: maxZoom });
        const start = initialCrop
          ? clampState(initialCrop, image, frame, bounds)
          : initialState(image, frame, bounds);
        setCrop(start);
        setPhase({ kind: 'ready', image, bounds });
      },
      () => { if (!cancelled) setPhase({ kind: 'error' }); }
    );
    return () => { cancelled = true; decoded?.close(); };
    // initialCrop is read once, on open: a parent re-rendering must not snap the picture back.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file, frame, mask, maxZoom]);

  // The picture area is as wide as the modal allows; its height follows the frame's shape.
  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const measure = () => setStageWidth(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [phase.kind]);

  const stage = useMemo<Size>(() => ({ width: stageWidth, height: stageWidth * (frame.height / frame.width) }), [stageWidth, frame]);
  /** Display pixels per output pixel. */
  const k = stage.width > 0 ? (stage.width * FRAME_SHARE) / frame.width : 0;
  const frameRect = useMemo<Rect>(() => {
    const width = frame.width * k;
    const height = frame.height * k;
    return { x: (stage.width - width) / 2, y: (stage.height - height) / 2, width, height };
  }, [frame, k, stage]);

  const ready = phase.kind === 'ready' ? phase : null;

  // Draw the picture where it sits. The frame, the dimming and the circle are DOM on top of it.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!ready || !canvas || k <= 0) return;
    const dpr = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 1;
    const w = Math.round(stage.width * dpr);
    const h = Math.round(stage.height * dpr);
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, stage.width, stage.height);
    const p = placement(ready.image, frame, crop, ready.bounds);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(ready.image.source, frameRect.x + p.x * k, frameRect.y + p.y * k, p.width * k, p.height * k);
  }, [ready, crop, stage, frame, frameRect, k]);

  // The small result, drawn by the same function as the file, so the preview is what is saved.
  useEffect(() => {
    const canvas = previewRef.current;
    if (!ready || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(canvas.width / frame.width, 0, 0, canvas.height / frame.height, 0, 0);
    drawCrop(ctx, ready.image, frame, crop, ready.bounds, background);
  }, [ready, crop, frame, background]);

  // Put the keyboard on the picture as soon as there is one, so the arrow keys work at once.
  useEffect(() => {
    if (ready) stageRef.current?.focus({ preventScroll: true });
  }, [ready]);

  const update = useCallback((fn: (s: CropState, r: NonNullable<typeof ready>) => CropState) => {
    if (!ready) return;
    setCrop((s) => fn(s, ready));
  }, [ready]);

  /** A point on screen as an anchor: output pixels from the frame's centre. */
  const anchorAt = useCallback((clientX: number, clientY: number): Point => {
    const el = stageRef.current;
    if (!el || k <= 0) return { x: 0, y: 0 };
    const box = el.getBoundingClientRect();
    return {
      x: (clientX - (box.left + frameRect.x + frameRect.width / 2)) / k,
      y: (clientY - (box.top + frameRect.y + frameRect.height / 2)) / k
    };
  }, [k, frameRect]);

  // The wheel needs a listener that may cancel scrolling, which React's own wheel handler cannot.
  useEffect(() => {
    const el = stageRef.current;
    if (!el || !ready) return;
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const unit = e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 400 : 1;
      const factor = Math.exp(-e.deltaY * unit * 0.0015);
      const a = anchorAt(e.clientX, e.clientY);
      setCrop((s) => zoomAt(s, s.zoom * factor, a, ready.image, frame, ready.bounds));
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [ready, anchorAt, frame]);

  function onPointerDown(e: PointerEvent<HTMLDivElement>) {
    if (!ready || (e.pointerType === 'mouse' && e.button !== 0)) return;
    e.currentTarget.setPointerCapture?.(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    setDragging(true);
  }

  function onPointerMove(e: PointerEvent<HTMLDivElement>) {
    const map = pointers.current;
    const prev = map.get(e.pointerId);
    if (!prev || !ready || k <= 0) return;
    const cur = { x: e.clientX, y: e.clientY };
    const ids = [...map.keys()];
    if (ids.length === 1) {
      map.set(e.pointerId, cur);
      const dx = (cur.x - prev.x) / k;
      const dy = (cur.y - prev.y) / k;
      update((s, r) => panBy(s, dx, dy, r.image, frame, r.bounds));
      return;
    }
    // A pinch: the first two pointers. Zoom by how far apart they moved, about the point between them, and follow it.
    const pair = ids.slice(0, 2);
    if (!pair.includes(e.pointerId)) { map.set(e.pointerId, cur); return; }
    const other = map.get(pair[0] === e.pointerId ? pair[1]! : pair[0]!)!;
    map.set(e.pointerId, cur);
    const before = Math.hypot(prev.x - other.x, prev.y - other.y);
    const after = Math.hypot(cur.x - other.x, cur.y - other.y);
    const m0 = { x: (prev.x + other.x) / 2, y: (prev.y + other.y) / 2 };
    const m1 = { x: (cur.x + other.x) / 2, y: (cur.y + other.y) / 2 };
    const ratio = before > 0 ? after / before : 1;
    const a = anchorAt(m1.x, m1.y);
    update((s, r) => {
      const zoomed = zoomAt(s, s.zoom * ratio, a, r.image, frame, r.bounds);
      return panBy(zoomed, (m1.x - m0.x) / k, (m1.y - m0.y) / k, r.image, frame, r.bounds);
    });
  }

  function onPointerEnd(e: PointerEvent<HTMLDivElement>) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size === 0) setDragging(false);
  }

  function zoomBy(factor: number) {
    update((s, r) => zoomAt(s, s.zoom * factor, { x: 0, y: 0 }, r.image, frame, r.bounds));
  }

  function reset() {
    update((_, r) => initialState(r.image, frame, r.bounds));
  }

  function onKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    if (!ready) return;
    const step = frame.width * (e.shiftKey ? 0.1 : 0.02);
    switch (e.key) {
      case 'ArrowLeft': update((s, r) => panBy(s, -step, 0, r.image, frame, r.bounds)); break;
      case 'ArrowRight': update((s, r) => panBy(s, step, 0, r.image, frame, r.bounds)); break;
      case 'ArrowUp': update((s, r) => panBy(s, 0, -step, r.image, frame, r.bounds)); break;
      case 'ArrowDown': update((s, r) => panBy(s, 0, step, r.image, frame, r.bounds)); break;
      case '+': case '=': zoomBy(1.1); break;
      case '-': case '_': zoomBy(1 / 1.1); break;
      case '0': reset(); break;
      default: return;
    }
    e.preventDefault();
  }

  const edge = ready ? sourceEdge(frame, crop, ready.bounds) : Infinity;
  const lowResolution = minSourceEdge > 0 && edge < minSourceEdge - 0.5;
  const sliderValue = ready ? Math.round(zoomToSlider(crop.zoom, ready.bounds) * 1000) : 0;
  const zoomPercent = Math.round(crop.zoom * 100);

  async function confirm() {
    if (!ready || busy) return;
    setBusy(true);
    setExportFailed(false);
    try {
      const blob = await renderCrop(ready.image, {
        width: frame.width, height: frame.height, state: crop, bounds: ready.bounds, background, type: outputType, quality
      });
      const name = outputFileName(fileName ?? (file instanceof File ? file.name : undefined), outputType);
      const out = new File([blob], name, { type: outputType, lastModified: Date.now() });
      await onConfirm({
        file: out,
        width: frame.width,
        height: frame.height,
        crop,
        source: cropRects(ready.image, frame, crop, ready.bounds).source,
        sourceEdge: edge,
        lowResolution
      });
    } catch {
      setExportFailed(true);
    } finally {
      setBusy(false);
    }
  }

  const circle = mask === 'circle';
  const frameStyle = { left: frameRect.x, top: frameRect.y, width: frameRect.width, height: frameRect.height };

  return (
    <div className="rr-cropper">
      <p id={instructionsId} className="rr-cropper__instructions">{labels.instructions}</p>

      {phase.kind === 'error' ? (
        <Alert color="error" role="alert">{labels.decodeError}</Alert>
      ) : (
        <div
          ref={stageRef}
          className={['rr-cropper__stage', dragging && 'rr-cropper__stage--dragging', !ready && 'rr-cropper__stage--loading'].filter(Boolean).join(' ')}
          style={{ aspectRatio: `${frame.width} / ${frame.height}` }}
          tabIndex={ready ? 0 : -1}
          role="application"
          aria-label={labels.stage}
          aria-describedby={lowResolution ? `${instructionsId} ${warningId}` : instructionsId}
          aria-busy={!ready}
          onKeyDown={onKeyDown}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerEnd}
          onPointerCancel={onPointerEnd}
          onLostPointerCapture={onPointerEnd}
        >
          {ready && k > 0 ? (
            <>
              <div className="rr-cropper__framebg" style={{ ...frameStyle, background }} aria-hidden="true" />
              <canvas ref={canvasRef} className="rr-cropper__canvas" aria-hidden="true" />
              <div className={`rr-cropper__mask${circle ? ' rr-cropper__mask--circle' : ''}`} style={frameStyle} aria-hidden="true" />
              {circle ? <div className="rr-cropper__outline" style={frameStyle} aria-hidden="true" /> : null}
            </>
          ) : (
            <div className="rr-cropper__loading">
              <Loader size="sm" />
              <span>{labels.loading}</span>
            </div>
          )}
        </div>
      )}

      {phase.kind !== 'error' ? (
        <div className="rr-cropper__controls">
          <div className={`rr-cropper__preview${circle ? ' rr-cropper__preview--circle' : ''}`}>
            <span className="rr-cropper__previewlabel">{labels.preview}</span>
            <canvas
              ref={previewRef}
              width={PREVIEW_PX * 2}
              height={Math.round((PREVIEW_PX * 2 * frame.height) / frame.width)}
              role="img"
              aria-label={labels.previewAlt}
              style={{ width: PREVIEW_PX, height: Math.round((PREVIEW_PX * frame.height) / frame.width) }}
            />
          </div>

          <div className="rr-cropper__zoomcol">
            <div className="rr-cropper__zoom">
              <ActionIcon variant="default" size="md" aria-label={labels.zoomOut} onClick={() => zoomBy(1 / 1.25)} disabled={!ready}>
                <Minus />
              </ActionIcon>
              <div className="rr-cropper__slider">
                <Slider
                  min={0}
                  max={1000}
                  step={1}
                  value={sliderValue}
                  disabled={!ready}
                  aria-label={labels.zoom}
                  aria-valuetext={fill(labels.zoomValue, { value: zoomPercent })}
                  onChange={(v) => update((s, r) => zoomAt(s, sliderToZoom(v / 1000, r.bounds), { x: 0, y: 0 }, r.image, frame, r.bounds))}
                />
              </div>
              <ActionIcon variant="default" size="md" aria-label={labels.zoomIn} onClick={() => zoomBy(1.25)} disabled={!ready}>
                <Plus />
              </ActionIcon>
            </div>
            <p className="rr-cropper__note">
              <span>{fill(labels.output, { width: frame.width, height: frame.height, format: formatName(outputType) })}</span>
              <span>{labels.fillNote}</span>
            </p>
          </div>
        </div>
      ) : null}

      {lowResolution ? (
        <Alert color="warning" id={warningId} role="status">
          {fill(labels.lowResolution, { edge: Math.max(1, Math.round(edge)), min: minSourceEdge })}
        </Alert>
      ) : null}

      {exportFailed ? <Alert color="error" role="alert">{labels.exportError}</Alert> : null}

      <div className="rr-cropper__footer">
        <Button variant="subtle" onClick={reset} disabled={!ready || busy}>{labels.reset}</Button>
        <div className="rr-cropper__actions">
          <Button variant="default" onClick={onCancel} disabled={busy}>{labels.cancel}</Button>
          <Button onClick={confirm} loading={busy} disabled={!ready}>{labels.confirm}</Button>
        </div>
      </div>
    </div>
  );
}
